/**
 * CSV preview parsing — for the document-detail preview pane only.
 *
 * Goal: turn raw CSV/TSV text into a 2D array of strings the table renderer
 * can show. The previous implementation split each row on a `/[,;]/` regex,
 * which broke European-formatted files where the field separator is `;` and
 * the decimal separator is `,` — e.g. `0,46326759` became two cells.
 *
 * This module picks a single delimiter by counting candidates over the first
 * few records, then reads records with a tiny RFC4180 state machine. A quoted
 * cell may hold delimiters, doubled quotes (`""`) and line breaks, so a record
 * ends only at a line break outside quotes — the way spreadsheet apps read an
 * Excel export with multi-line cells.
 */

const CANDIDATE_DELIMITERS = [';', '\t', ',', '|'] as const

type Delimiter = (typeof CANDIDATE_DELIMITERS)[number]

const HEADER_SAMPLE_RECORDS = 5

// Detection reads only the start of the file: an unbalanced quote would
// otherwise send it through the whole file looking for its sample records.
const DETECTION_SAMPLE_CHARS = 64 * 1024

// A longer cell is cut, so a quote that never closes cannot turn the rest of a
// large file into one cell held in memory and drawn whole.
const MAX_CELL_CHARS = 10_000

/**
 * The first `count` non-blank records with their quoted parts left out, so a
 * delimiter or line break inside a quoted cell neither counts nor splits.
 */
function unquotedRecords(text: string, count: number): string[] {
  const records: string[] = []
  let record = ''
  let inQuotes = false
  for (let i = 0; i < text.length && records.length < count; i++) {
    const ch = text[i]
    if (ch === '"') inQuotes = !inQuotes
    else if (!inQuotes) {
      if (ch === '\n' || ch === '\r') {
        if (record.trim()) records.push(record)
        record = ''
      } else record += ch
    }
  }
  if (records.length < count && record.trim()) records.push(record)
  return records
}

/**
 * Pick the candidate found in the most of the first few records (outside
 * quotes), the median count per record breaking ties. Falls back to `,` when
 * no candidate appears.
 */
export function detectCsvDelimiter(text: string): Delimiter {
  const records = unquotedRecords(text.slice(0, DETECTION_SAMPLE_CHARS), HEADER_SAMPLE_RECORDS)
  let best: Delimiter = ','
  let bestScore = -1
  for (const d of CANDIDATE_DELIMITERS) {
    const counts = records.map(r => r.split(d).length - 1)
    const recordsWithIt = counts.filter(c => c > 0).length
    if (recordsWithIt === 0) continue
    const median = [...counts].sort((a, b) => a - b)[Math.floor(counts.length / 2)]
    const score = recordsWithIt * 100 + median
    if (score > bestScore) {
      bestScore = score
      best = d
    }
  }
  return best
}

/**
 * Parse the first `maxRows` non-blank records of a CSV/TSV blob into a 2D
 * string array suitable for the preview table. A `"` opens a quoted cell only
 * at the start of a cell; anywhere else it is an ordinary character, so a
 * stray quote cannot swallow the rest of the file.
 */
export function parseCsvPreview(text: string, maxRows = 20): string[][] {
  const delimiter = detectCsvDelimiter(text)
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false

  const append = (s: string) => {
    if (cell.length < MAX_CELL_CHARS) cell += s
  }
  const endCell = () => {
    row.push(cell.length < MAX_CELL_CHARS ? cell.trim() : `${cell.trim()}…`)
    cell = ''
  }
  const endRow = () => {
    endCell()
    // A blank line is a single empty cell and no row; `;;` is a row of empty cells.
    if (row.length > 1 || row[0] !== '') rows.push(row)
    row = []
  }

  for (let i = 0; i < text.length && rows.length < maxRows; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch !== '"') append(ch)
      else if (text[i + 1] === '"') {
        append('"')
        i++
      } else inQuotes = false
    } else if (ch === '"' && cell.trim() === '') {
      // Whitespace before the opening quote (and a leading BOM) is not part of the cell.
      inQuotes = true
      cell = ''
    } else if (ch === delimiter) endCell()
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      endRow()
    } else append(ch)
  }
  if (rows.length < maxRows) endRow()
  return rows
}
